import { html } from '@codemirror/lang-html'
import CodeMirror from '@uiw/react-codemirror'
import { formatDistanceToNow } from 'date-fns'
import { AlertTriangle, ArrowLeft, ChevronDown, ChevronRight, Clock, Globe, Save } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import {
  useCreateTemplate,
  usePublishTemplate,
  useTemplate,
  useUpdateTemplate,
} from '@/api/hooks/use-templates'
import { StatusBadge } from '@/components/ui/status-badge'
import { CustomizeTab } from '@/pages/template-editor-customize'
import { TemplatePreviewTab } from '@/pages/template-editor-preview'
// Block editor — commented out for now, revisit later
// import { BlockEditorTab } from '@/pages/template-editor-blocks'
import { TemplatePicker } from '@/pages/template-picker'
import type { SlotConfig } from '@/pages/template-slot-panel'
import { TemplateSlotPanel } from '@/pages/template-slot-panel'
import type { StockTemplate } from '@/pages/template-stock'
import { STOCK_TEMPLATES } from '@/pages/template-stock'

type EditorTab = 'code' | 'preview' | 'customize'

const BLANK_HTML = STOCK_TEMPLATES.find((t) => t.id === 'blank')?.html ?? ''
const DEFAULT_SAMPLE_DATA = JSON.stringify({ company_name: 'Acme Inc', name: 'John' }, null, 2)

const UNSUB_FOOTER = `\n      <!-- Unsubscribe footer -->\n      <tr>\n        <td style="padding:24px 32px; text-align:center; background:#111111;">\n          <p style="margin:0 0 8px; font-size:13px; color:#d4d4d8;">You received this because you subscribed to {{company_name}}.</p>\n          <a href="{{unsubscribe_url}}" style="color:#f97316; font-size:13px; text-decoration:underline;">Unsubscribe</a>\n        </td>\n      </tr>`

export function TemplateEditorPage(): React.JSX.Element {
  const { id } = useParams()
  const navigate = useNavigate()
  const isCreateMode = !id

  const { data: existingTemplate, isLoading } = useTemplate(id ?? '')
  const createTemplate = useCreateTemplate()
  const updateTemplate = useUpdateTemplate()
  const publishTemplate = usePublishTemplate()

  const [pickerDone, setPickerDone] = useState(false)
  const [activeTab, setActiveTab] = useState<EditorTab>('preview')
  const [name, setName] = useState('')
  const [subject, setSubject] = useState('')
  const [htmlContent, setHtmlContent] = useState(BLANK_HTML)
  const [sampleData, setSampleData] = useState(DEFAULT_SAMPLE_DATA)
  const [showDataPanel, setShowDataPanel] = useState(false)
  const [showVersions, setShowVersions] = useState(false)
  const [slots, setSlots] = useState<SlotConfig[]>([])

  // Auto-detect {{variable}} patterns in the HTML
  const detectedVars = useMemo(() => {
    const matches = htmlContent.match(/\{\{(\w+)\}\}/g)
    if (!matches) return []
    const unique = new Set(matches.map((m) => m.slice(2, -2)))
    unique.delete('unsubscribe_url')
    unique.delete('subject')
    return Array.from(unique)
  }, [htmlContent])

  // Auto-populate template data with detected variables
  useEffect(() => {
    if (detectedVars.length === 0) return
    try {
      const current = JSON.parse(sampleData)
      let changed = false
      for (const v of detectedVars) {
        if (!(v in current)) { current[v] = ''; changed = true }
      }
      if (changed) setSampleData(JSON.stringify(current, null, 2))
    } catch { /* invalid JSON */ }
  }, [detectedVars, sampleData])

  useEffect(() => {
    if (existingTemplate) {
      setName(existingTemplate.name ?? '')
      setSubject(existingTemplate.subject ?? '')
      setHtmlContent(existingTemplate.html ?? BLANK_HTML)
      setPickerDone(true)
    }
  }, [existingTemplate])

  function handlePickTemplate(template: StockTemplate): void {
    setName(template.name)
    setSubject(template.subject)
    setHtmlContent(template.html)
    setPickerDone(true)
  }

  function handlePickBlank(): void {
    setHtmlContent(BLANK_HTML)
    setPickerDone(true)
  }

  function handleSaveDraft(): void {
    if (isCreateMode) {
      createTemplate.mutate(
        { name: name || 'Untitled Template', subject, html: htmlContent },
        { onSuccess: (result) => navigate(`/templates/${result.id}`, { replace: true }) },
      )
    } else {
      if (!id) return
      updateTemplate.mutate({ id, data: { name, subject, html: htmlContent } })
    }
  }

  function handleInsertUnsub(): void {
    const insertBefore = '</table>\n</body>'
    if (htmlContent.includes(insertBefore)) {
      setHtmlContent(htmlContent.replace(insertBefore, `${UNSUB_FOOTER}\n    ${insertBefore}`))
    } else {
      setHtmlContent(`${htmlContent}\n${UNSUB_FOOTER}`)
    }
  }

  const isSaving = createTemplate.isPending || updateTemplate.isPending
  const isPublishing = publishTemplate.isPending
  const hasUnsub = htmlContent.includes('{{unsubscribe_url}}')

  if (!isCreateMode && isLoading) {
    return <div className="py-12 text-center text-text-tertiary">Loading template...</div>
  }

  if (isCreateMode && !pickerDone) {
    return <TemplatePicker onSelect={handlePickTemplate} onBlank={handlePickBlank} />
  }

  const tabs: { id: EditorTab; label: string }[] = [
    { id: 'preview', label: 'Preview' },
    { id: 'code', label: 'Code' },
    { id: 'customize', label: 'Customize' },
  ]

  return (
    <div className="space-y-4">
      {/* Top bar */}
      <div className="flex flex-col gap-3 border-b border-surface-3 pb-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <Link to="/templates" className="rounded-lg p-1.5 hover:bg-surface-2">
            <ArrowLeft className="h-5 w-5 text-text-secondary" />
          </Link>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Template name" className="rounded-lg border border-surface-3 bg-surface-0 px-3 py-1.5 text-sm font-medium text-text-primary placeholder:text-text-tertiary focus:border-brand-500 focus:outline-none" />
          <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject line" className="rounded-lg border border-surface-3 bg-surface-0 px-3 py-1.5 text-sm text-text-primary placeholder:text-text-tertiary focus:border-brand-500 focus:outline-none" />
        </div>
        <div className="flex items-center gap-2">
          {existingTemplate && (
            <StatusBadge status={existingTemplate.status === 'published' ? 'active' : 'pending'}>
              {existingTemplate.status === 'published' ? 'Published' : 'Draft'}
            </StatusBadge>
          )}
          <button type="button" onClick={handleSaveDraft} disabled={isSaving} className="inline-flex items-center gap-1.5 rounded-lg border border-surface-3 px-3 py-1.5 text-sm font-medium text-text-primary hover:bg-surface-1 disabled:opacity-50">
            <Save className="h-4 w-4" />
            {isSaving ? 'Saving...' : 'Save Draft'}
          </button>
          {!isCreateMode && (
            <button type="button" onClick={() => publishTemplate.mutate(id!)} disabled={isPublishing} className="inline-flex items-center gap-1.5 rounded-lg bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50">
              <Globe className="h-4 w-4" />
              {isPublishing ? 'Publishing...' : 'Publish'}
            </button>
          )}
        </div>
      </div>

      {/* Unsub warning */}
      {!hasUnsub && (
        <div className="flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
            <p className="text-sm text-amber-800">
              Missing <code className="rounded bg-amber-100 px-1 font-mono text-xs">{'{{unsubscribe_url}}'}</code> — required for marketing emails.
            </p>
          </div>
          <button type="button" onClick={handleInsertUnsub} className="shrink-0 rounded-lg border border-amber-300 bg-white px-3 py-1 text-xs font-medium text-amber-700 hover:bg-amber-50">
            Insert footer
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-surface-3">
        {tabs.map((tab) => (
          <button key={tab.id} type="button" onClick={() => setActiveTab(tab.id)} className={activeTab === tab.id ? 'border-b-2 border-brand-500 px-4 py-2 text-sm font-medium text-brand-500' : 'px-4 py-2 text-sm font-medium text-text-tertiary hover:text-text-primary'}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'code' && (
        <div className="overflow-hidden rounded-xl border border-surface-3 bg-white">
          <CodeMirror value={htmlContent} onChange={setHtmlContent} extensions={[html()]} height="500px" basicSetup={{ lineNumbers: true, foldGutter: true, autocompletion: true, bracketMatching: true }} />
        </div>
      )}

      {activeTab === 'preview' && <TemplatePreviewTab htmlContent={htmlContent} sampleData={sampleData} />}

      {activeTab === 'customize' && <CustomizeTab htmlContent={htmlContent} onHtmlChange={setHtmlContent} />}

      {/* Block editor — commented out, revisit later */}
      {/* {activeTab === 'blocks' && <BlockEditorTab htmlContent={htmlContent} onHtmlChange={setHtmlContent} />} */}

      {/* Collapsible panels */}
      <CollapsiblePanel title={`Template Data${detectedVars.length > 0 ? ` (${detectedVars.length} variables)` : ''}`} open={showDataPanel} onToggle={() => setShowDataPanel(!showDataPanel)}>
        <p className="mb-2 text-xs text-text-secondary">
          {detectedVars.length > 0
            ? `Detected variables: ${detectedVars.join(', ')}. Fill in sample values below.`
            : 'Provide sample JSON data to preview template variables.'}
        </p>
        <textarea value={sampleData} onChange={(e) => setSampleData(e.target.value)} rows={5} spellCheck={false} className="w-full rounded-lg border border-surface-3 bg-surface-1 px-3 py-2 font-mono text-sm text-text-primary focus:border-brand-500 focus:outline-none" placeholder='{ "company_name": "Acme", "name": "John" }' />
      </CollapsiblePanel>

      <TemplateSlotPanel html={htmlContent} slots={slots} onSlotsChange={setSlots} />

      {!isCreateMode && existingTemplate && (
        <CollapsiblePanel title="Version History" open={showVersions} onToggle={() => setShowVersions(!showVersions)}>
          <div className="flex items-center justify-between rounded-lg bg-surface-1 px-3 py-2">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-text-tertiary" />
              <span className="text-sm font-medium text-text-primary">Version {existingTemplate.version}</span>
              <StatusBadge status={existingTemplate.status === 'published' ? 'active' : 'pending'}>
                {existingTemplate.status === 'published' ? 'Published' : 'Draft'}
              </StatusBadge>
            </div>
            <span className="text-xs text-text-tertiary">{formatDistanceToNow(new Date(existingTemplate.updated_at), { addSuffix: true })}</span>
          </div>
          {existingTemplate.version > 1 && (
            <p className="mt-2 px-3 text-xs text-text-tertiary">{existingTemplate.version - 1} earlier version{existingTemplate.version > 2 ? 's' : ''} available</p>
          )}
        </CollapsiblePanel>
      )}
    </div>
  )
}

function CollapsiblePanel({ title, open, onToggle, children }: { title: string; open: boolean; onToggle: () => void; children: React.ReactNode }): React.JSX.Element {
  return (
    <div className="rounded-xl border border-surface-3 bg-surface-0">
      <button type="button" onClick={onToggle} className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-surface-1">
        <span className="text-sm font-medium text-text-primary">{title}</span>
        {open ? <ChevronDown className="h-4 w-4 text-text-tertiary" /> : <ChevronRight className="h-4 w-4 text-text-tertiary" />}
      </button>
      {open && <div className="border-t border-surface-3 p-4">{children}</div>}
    </div>
  )
}
